'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelectPositionerContext } from '../positioner/SelectPositionerContext.js';
import { useSelectRootContext } from '../root/SelectRootContext.js';
import { useComponentRenderer } from '../../utils/useComponentRenderer.js';
import { useForkRef } from '../../utils/useForkRef.js';
import { mergeProps } from '../../merge-props/index.js';
import { popupStateMapping } from '../../utils/popupStateMapping.js';
/**
 * Displays an element positioned against the select menu anchor.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Select](https://base-ui.com/react/components/select)
 */
const SelectArrow = /*#__PURE__*/React.forwardRef(function SelectArrow(props, forwardedRef) {
  const {
    className,
    render,
    ...otherProps
  } = props;
  const {
    open,
    alignItemToTrigger
  } = useSelectRootContext();
  const {
    arrowRef,
    side,
    align,
    arrowUncentered,
    arrowStyles
  } = useSelectPositionerContext();
  const getArrowProps = React.useCallback((externalProps = {}) => mergeProps({
    style: arrowStyles,
    'aria-hidden': true
  }, externalProps), [arrowStyles]);
  const state = React.useMemo(() => ({
    open,
    side,
    align,
    uncentered: arrowUncentered
  }), [open, side, align, arrowUncentered]);
  const mergedRef = useForkRef(arrowRef, forwardedRef);
  const {
    renderElement
  } = useComponentRenderer({
    propGetter: getArrowProps,
    render: render ?? 'div',
    className,
    state,
    ref: mergedRef,
    extraProps: otherProps,
    customStyleHookMapping: popupStateMapping
  });
  if (alignItemToTrigger) {
    return null;
  }
  return renderElement();
});
process.env.NODE_ENV !== "production" ? SelectArrow.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * CSS class applied to the element, or a function that
   * returns a class based on the component’s state.
   */
  className: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Allows you to replace the component’s HTML element
   * with a different tag, or compose it with another component.
   *
   * Accepts a `ReactElement` or a function that returns the element to render.
   */
  render: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
} : void 0;
export { SelectArrow };